import { useEffect } from "react";
import {
  IconAlertTriangle,
  IconCheck,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import { Progress } from "@/components/ui/progress";
import {
  dismissUpload,
  retryUpload,
  useUploadJobs,
  type UploadJob,
} from "@/lib/upload-store";

export function UploadTray() {
  const jobs = useUploadJobs();
  const hasActive = jobs.some(
    (j) => j.status === "uploading" || j.status === "queued",
  );

  // Don't let a refresh/close silently kill in-flight uploads.
  useEffect(() => {
    if (!hasActive) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasActive]);

  if (jobs.length === 0) return null;

  const activeCount = jobs.filter(
    (j) => j.status === "uploading" || j.status === "queued",
  ).length;

  return (
    <div
      data-slot="upload-tray"
      className="fixed bottom-4 right-4 z-50 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-border bg-card shadow-lg"
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-xs font-semibold">
          Uploads{activeCount > 0 ? ` · ${activeCount} active` : ""}
        </span>
        {!hasActive && (
          <button
            onClick={() => jobs.forEach((j) => dismissUpload(j.id))}
            className="text-[11px] text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        )}
      </div>
      <div className="max-h-64 divide-y divide-border/60 overflow-y-auto">
        {jobs.map((job) => (
          <UploadRow key={job.id} job={job} />
        ))}
      </div>
    </div>
  );
}

function UploadRow({ job }: { job: UploadJob }) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2">
      <img
        src={job.previewUrl}
        alt=""
        className="h-9 w-9 shrink-0 rounded border border-border object-cover"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-xs font-medium">{job.label}</span>
          {job.status === "uploading" && (
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {job.progress}%
            </span>
          )}
        </div>
        {job.status === "queued" && (
          <span className="text-[11px] text-muted-foreground">Queued…</span>
        )}
        {job.status === "uploading" && (
          <Progress value={job.progress} className="mt-1 h-1" />
        )}
        {job.status === "done" && (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
            <IconCheck className="h-3 w-3" /> Saved
          </span>
        )}
        {job.status === "error" && (
          <span className="inline-flex items-center gap-1 text-[11px] text-destructive">
            <IconAlertTriangle className="h-3 w-3" /> {job.error ?? "Failed"}
          </span>
        )}
      </div>
      {job.status === "error" && (
        <button
          onClick={() => retryUpload(job.id)}
          className="shrink-0 text-muted-foreground hover:text-foreground"
          title="Retry"
        >
          <IconRefresh className="h-3.5 w-3.5" />
        </button>
      )}
      {(job.status === "done" || job.status === "error") && (
        <button
          onClick={() => dismissUpload(job.id)}
          className="shrink-0 text-muted-foreground hover:text-foreground"
          title="Dismiss"
        >
          <IconX className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
